import * as React from "react";
import { makeStyles, Button, Dialog, DialogSurface, DialogBody, Spinner } from "@fluentui/react-components";
import { Dismiss24Regular, Checkmark24Regular } from "@fluentui/react-icons";
import { Paper } from "../../types/paper";

interface DiagnosticsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  papers: Paper[];
}

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: "400px",
    width: "90%",
    maxHeight: "70vh",
    height: "auto",
    padding: "0",
    display: "flex",
    flexDirection: "column",
  },
  dialogHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 16px 12px",
    borderBottom: "1px solid #e8e8e8",
    flexShrink: "0",
  },
  dialogTitle: {
    fontSize: "16px",
    fontWeight: "600",
    margin: "0",
    flex: "1",
    textAlign: "center",
    paddingRight: "32px",
  },
  closeButton: {
    minWidth: "32px",
    minHeight: "32px",
    padding: "4px",
    borderRadius: "4px",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    color: "#666666",
    ":hover": {
      backgroundColor: "#f5f5f5",
      color: "#333333",
    },
  },
  dialogContent: {
    padding: "20px",
    overflowY: "auto",
    flex: "1",
    minHeight: "0",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  statCard: {
    padding: "16px",
    backgroundColor: "#f0f4ff",
    borderRadius: "12px",
    border: "2px solid #667eea",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  statCardSuccess: {
    padding: "16px",
    backgroundColor: "#ecfdf5",
    borderRadius: "12px",
    border: "2px solid #10b981",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  statLabel: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#4a5568",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  statValue: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#667eea",
    lineHeight: "1",
  },
  statValueSuccess: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#10b981",
    lineHeight: "1",
  },
  statDescription: {
    fontSize: "13px",
    color: "#718096",
    lineHeight: "1.4",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    padding: "40px 20px",
  },
  loadingText: {
    fontSize: "13px",
    color: "#718096",
  },
  healthIcon: {
    fontSize: "48px",
    marginBottom: "8px",
  },
});

const DiagnosticsPanel: React.FC<DiagnosticsPanelProps> = ({ isOpen, onClose, papers }) => {
  const styles = useStyles();
  const [stats, setStats] = React.useState<{
    totalCitations: number;
    uniquePapers: number;
    recentCitations: number;
    isLoading: boolean;
  }>({
    totalCitations: 0,
    uniquePapers: 0,
    recentCitations: 0,
    isLoading: true,
  });

  React.useEffect(() => {
    if (!isOpen) return;

    const fetchDiagnostics = async () => {
      setStats(prev => ({ ...prev, isLoading: true }));
      
      try {
        await Word.run(async (context) => {
          // Get all citation content controls
          const singleControls = context.document.contentControls.getByTag("scholarsync-citation");
          const multiControls = context.document.contentControls.getByTag("scholarsync-citation-multiple");
          
          singleControls.load("items");
          multiControls.load("items");
          
          await context.sync();
          
          // Extract paper IDs to count both total and unique papers
          const paperIds: string[] = [];
          
          // Single citations
          for (let i = 0; i < singleControls.items.length; i++) {
            const control = singleControls.items[i];
            control.load("title");
            await context.sync();
            if (control.title) {
              paperIds.push(control.title);
            }
          }
          
          // Multi-citations
          for (let i = 0; i < multiControls.items.length; i++) {
            const control = multiControls.items[i];
            control.load("title");
            await context.sync();
            if (control.title) {
              const ids = control.title.split(',');
              paperIds.push(...ids);
            }
          }
          
          // Total citations = all paper IDs (including duplicates)
          const totalCitations = paperIds.length;
          
          // Get unique paper IDs
          const uniquePaperIds = Array.from(new Set(paperIds));
          const uniquePapers = uniquePaperIds.length;
          
          // Count papers published after 2020
          const recentCitations = uniquePaperIds.filter(id => {
            const paper = papers.find(p => p.id === id);
            return paper && paper.year > 2020;
          }).length;
          
          setStats({
            totalCitations,
            uniquePapers,
            recentCitations,
            isLoading: false,
          });
        });
      } catch (error) {
        console.error("Error fetching diagnostics:", error);
        setStats({
          totalCitations: 0,
          uniquePapers: 0,
          recentCitations: 0,
          isLoading: false,
        });
      }
    };

    fetchDiagnostics();
  }, [isOpen, papers]);

  return (
    <Dialog open={isOpen} onOpenChange={(_event, data) => !data.open && onClose()}>
      <DialogSurface className={styles.dialogSurface}>
        <div className={styles.dialogHeader}>
          <h2 className={styles.dialogTitle}>📊 Citation Insights</h2>
          <Button
            appearance="transparent"
            icon={<Dismiss24Regular />}
            className={styles.closeButton}
            onClick={onClose}
            title="Close"
          />
        </div>
        <DialogBody className={styles.dialogContent}>
          {stats.isLoading ? (
            <div className={styles.loadingContainer}>
              <Spinner size="large" />
              <span className={styles.loadingText}>Analyzing your document...</span>
            </div>
          ) : (
            <>
              {stats.totalCitations > 0 && (
                <div style={{ textAlign: "center" }}>
                  <span className={styles.healthIcon}>
                    <Checkmark24Regular style={{ fontSize: "48px", color: "#10b981" }} />
                  </span>
                </div>
              )}
              
              <div className={styles.statCard}>
                <div className={styles.statLabel}>Total Citations Inserted</div>
                <div className={styles.statValue}>{stats.totalCitations}</div>
                <div className={styles.statDescription}>
                  {stats.totalCitations === 0 
                    ? "No citations found in this document"
                    : stats.uniquePapers === stats.totalCitations
                      ? `${stats.uniquePapers} unique ${stats.uniquePapers === 1 ? 'paper' : 'papers'} cited`
                      : `${stats.uniquePapers} unique ${stats.uniquePapers === 1 ? 'paper' : 'papers'} cited`}
                </div>
              </div>

              <div className={styles.statCardSuccess}>
                <div className={styles.statLabel}>Recent Citations (After 2020)</div>
                <div className={styles.statValueSuccess}>{stats.recentCitations}</div>
                <div className={styles.statDescription}>
                  {stats.uniquePapers > 0 
                    ? `${Math.round((stats.recentCitations / stats.uniquePapers) * 100)}% of your unique citations are from after 2020`
                    : "Start citing papers to see statistics"}
                </div>
              </div>
            </>
          )}
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default DiagnosticsPanel;
